import type { GaugeLook, GaugeStyle, Threshold } from '@casa/model';
import { formatNumber } from '@casa/util';
import { sampleColors } from './color_gradient.ts';
import { drawText, inkHang, layoutText, type FontMetrics, type Rect, type TextStyle } from './flex_label.ts';
import { drawIcon, type IconDefinition } from './icon.ts';

/**
 * The gauge's styles beside the legacy dial (docs/specs/gauge-styles.md): each draws its scale and
 * its texts, the number with its unit, the icon, and the second value as a number where there is
 * room (the owner: "Even if the gauge has a visual representation of the secondary value, it should
 * still show that value as a number"). `ticks`, the legacy dial, is drawn by `gauge.ts` as before.
 * Angles here are degrees clockwise from twelve o'clock.
 */

type Context = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** One value on the gauge: the first, the second, and more (the rings). */
export interface GaugeReading {
    readonly value: number | null;
    readonly min: number;
    readonly max: number;
    /** Its own colour; the scale's at its place when left out (the first value). */
    readonly color?: string | undefined;
    /** As shown: formatted, with its unit. */
    readonly text?: string | undefined;
}

export interface StyledGauge {
    readonly style: Exclude<GaugeStyle, 'ticks'>;
    /** The first value, the second (if any), then more. */
    readonly readings: readonly GaugeReading[];
    /** Whether the second value is in `readings[1]`. */
    readonly hasSecond: boolean;
    /** Whether the second value is also marked on the scale: only where it is on the same scale (a set point). */
    readonly marksSecond: boolean;
    readonly numberText: string;
    readonly unitText: string;
    readonly unitScale: number;
    /** The number's colour; the second value's is its reading's. */
    readonly color: string;
    readonly icon?: IconDefinition | undefined;
    readonly iconColor: string;
    /** The gradient along the scale. */
    readonly colors: readonly string[];
    readonly thresholds: readonly Threshold[];
    readonly coloring: 'gradient' | 'threshold';
    /** The part of the scale not filled; `none` for none, a style's own when left out. */
    readonly track?: string | undefined;
    readonly look: GaugeLook;
    readonly locale?: string | undefined;
    readonly font: { readonly family: string; readonly metrics: FontMetrics };
}

const DEFAULT_TRACK = 'rgba(255, 255, 255, 0.16)';
const DIM = '#9a9a9a';
/** The rings' colours, in turn, for values without one (a smart watch's red, green, blue, …). */
export const RING_COLORS = ['#ff375f', '#30d158', '#0a84ff', '#ffd60a', '#bf5af2'] as const;

// ---- Helpers --------------------------------------------------------------------------------------

const toRad = (degrees: number) : number => ((degrees - 90) * Math.PI) / 180;
const point = (cx: number, cy: number, r: number, degrees: number) : [number, number] => [cx + r * Math.cos(toRad(degrees)), cy + r * Math.sin(toRad(degrees))];
const clamp01 = (value: number) : number => Math.min(1, Math.max(0, value));

/** Where a reading lies on its scale, 0 to 1; undefined without a value or a scale. */
export function positionOf(reading: GaugeReading | undefined) : number | undefined {
    if (!reading || reading.value === null || !(reading.max > reading.min)) {
        return undefined;
    }

    return clamp01((reading.value - reading.min) / (reading.max - reading.min));
}

/** The colour of a threshold at `value`: the last one it has reached, else the first. */
export function thresholdColor(thresholds: readonly Threshold[], value: number) : string | undefined {
    const sorted = [...thresholds].sort((a, b) => a.from - b.from);
    let color = sorted[0]?.color;
    for (const threshold of sorted) {
        if (value >= threshold.from) {
            color = threshold.color;
        }
    }

    return color;
}

const sampled = new WeakMap<readonly string[], string[]>();

/** The scale's colour at a place on it (0 to 1): along the gradient, or the threshold's there. */
export function scaleColor(gauge: Pick<StyledGauge, 'colors' | 'thresholds' | 'coloring' | 'readings'>, position: number) : string {
    const first = gauge.readings[0];
    if (gauge.coloring === 'threshold' && gauge.thresholds.length > 0 && first) {
        return thresholdColor(gauge.thresholds, first.min + (first.max - first.min) * clamp01(position)) ?? '#ffffff';
    }

    let colors = sampled.get(gauge.colors);
    if (!colors) {
        colors = sampleColors(gauge.colors, 256);
        sampled.set(gauge.colors, colors);
    }

    return colors[Math.round(clamp01(position) * (colors.length - 1))] ?? '#ffffff';
}

/** Where the second value is marked on the scale; undefined where it is not (another quantity's number). */
const secondPlace = (gauge: StyledGauge) : number | undefined => (gauge.hasSecond && gauge.marksSecond ? positionOf(gauge.readings[1]) : undefined);

/** A reading's colour: its own, else the scale's at its place. */
const readingColor = (gauge: StyledGauge, reading: GaugeReading | undefined) : string => reading?.color ?? scaleColor(gauge, positionOf(reading) ?? 0);

const trackOf = (gauge: StyledGauge, fallback = DEFAULT_TRACK) : string | undefined => (gauge.track === 'none' ? undefined : (gauge.track ?? fallback));

function ink(gauge: StyledGauge, options: Partial<TextStyle> = {}) : TextStyle {
    // A text too wide for its box is made smaller, not squeezed (narrow portrait tiles).
    return { family: gauge.font.family, metrics: gauge.font.metrics, fill: true, fitWidth: false, ...options };
}

/** A scale number (minimum, maximum, a needle's): as given, without needless decimals. */
const scaleText = (gauge: StyledGauge, value: number) : string => formatNumber(value, '0.##', gauge.locale);

function stroke(ctx: Context, color: string, width: number, cap: CanvasLineCap = 'butt') : void {
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = cap;
    ctx.stroke();
}

function arcPath(ctx: Context, cx: number, cy: number, r: number, from: number, to: number) : void {
    ctx.beginPath();
    ctx.arc(cx, cy, r, toRad(from), toRad(to));
}

/** An arc from `from` to `to` degrees, coloured along the scale between scale places `p0` and `p1`. */
function scaleArc(ctx: Context, gauge: StyledGauge, cx: number, cy: number, r: number, from: number, to: number, p0: number, p1: number, width: number) : void {
    const pieces = Math.max(1, Math.ceil(Math.abs(to - from) / 2));
    for (let index = 0; index < pieces; index++) {
        const a = from + ((to - from) * index) / pieces;
        const b = from + ((to - from) * (index + 1)) / pieces;
        // A hair of overlap, so that the pieces do not show seams.
        arcPath(ctx, cx, cy, r, a, Math.min(to, b + 0.4));
        stroke(ctx, scaleColor(gauge, p0 + ((p1 - p0) * (index + 0.5)) / pieces), width);
    }
}

function roundCap(ctx: Context, x: number, y: number, width: number, color: string) : void {
    ctx.beginPath();
    ctx.arc(x, y, width / 2, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
}

/** A dial of `sweep` degrees, as large as fits `box` with room below for `below`: its centre and outer radius. */
function fitDial(box: Rect, sweep: number, below = 0) : { cx: number; cy: number; radius: number } {
    const half = (Math.min(360, sweep) / 2) * (Math.PI / 180);
    const down = sweep >= 360 ? 1 : Math.max(0, -Math.cos(half));
    const radius = Math.max(0, Math.min(box.width / 2, (box.height - below) / (1 + down)) * 0.97);
    const used = radius * (1 + down) + below;

    return { cx: box.left + box.width / 2, cy: box.top + (box.height - used) / 2 + radius, radius };
}

/**
 * The texts in a dial's middle: the icon above, the number, its unit below it, and the second value
 * below that unless it has a place at the bottom (`withSecond` false). `size` is the free radius
 * inside the dial.
 */
function centreTexts(ctx: Context, gauge: StyledGauge, cx: number, cy: number, fullSize: number, within?: Rect, withSecond = true) : void {
    const hasSecondLine = withSecond && hasSecondText(gauge);
    const numberStyle = ink(gauge, { valign: 'bottom' });
    // Below the number, a comma's descender (always, so that a number with one and without are as large), in parts of `size`.
    const hang = 0.5 * inkHang(ctx, '0,', numberStyle);
    // The whole stack centred on the dial's middle, so that a unit and a second value stay inside it;
    // moved up, then made smaller, where it would leave `within` (an arc's open bottom).
    const heightOf = (size: number) : number => (gauge.icon ? size * 0.39 : 0) + size * (0.5 + hang) + (gauge.unitText === '' ? 0 : size * (0.1 + 0.19 * gauge.unitScale)) + (hasSecondLine ? size * 0.29 : 0);
    let size = fullSize;
    if (within && heightOf(size) > within.height) {
        size *= within.height / heightOf(size);
    }
    if (within && cy + heightOf(size) / 2 > within.top + within.height) {
        cy = within.top + within.height - heightOf(size) / 2;
    }
    const numberHeight = size * 0.5;
    const numberWidth = size * 1.5;
    const iconSide = gauge.icon ? size * 0.3 : 0;
    const unitHeight = gauge.unitText === '' ? 0 : size * 0.19 * gauge.unitScale;
    const secondHeight = hasSecondLine ? size * 0.17 : 0;
    const total = (iconSide > 0 ? iconSide * 1.3 : 0) + numberHeight + size * hang + (unitHeight > 0 ? size * 0.1 + unitHeight : 0) + (secondHeight > 0 ? size * 0.12 + secondHeight : 0);
    const top = cy - total / 2 + (iconSide > 0 ? iconSide * 1.3 : 0);
    if (gauge.icon) {
        drawIcon(ctx, gauge.icon, { left: cx - iconSide / 2, top: top - iconSide * 1.3, width: iconSide, height: iconSide }, gauge.iconColor, { fill: true });
    }
    drawText(ctx, gauge.numberText, { left: cx - numberWidth / 2, top, width: numberWidth, height: numberHeight }, gauge.color, numberStyle);
    let below = top + numberHeight + size * hang;
    if (unitHeight > 0) {
        below += size * 0.1;
        drawText(ctx, gauge.unitText, { left: cx - size, top: below, width: size * 2, height: unitHeight }, gauge.color, ink(gauge, { valign: 'top' }));
        below += unitHeight;
    }
    if (hasSecondLine) {
        secondText(ctx, gauge, { left: cx - size * 0.8, top: below + size * 0.12, width: size * 1.6, height: secondHeight });
    }
}

/** Whether there is a second value to show as a number. */
function hasSecondText(gauge: StyledGauge) : boolean {
    return gauge.hasSecond && (gauge.readings[1]?.text ?? '') !== '';
}

/**
 * The second value at the bottom, in the open part of a dial between its ends, as the legacy dial
 * has it (the owner, 2026-10-22). False where the dial is too closed for it (a sweep above 300°)
 * or too open (180° or less, whose ends are not below the middle): the caller keeps it in the middle.
 * `bottom` is where its foot goes, else the dial's lowest point; `halfWidth` limits it (the arc's
 * minimum and maximum beside it).
 */
function gapSecond(ctx: Context, gauge: StyledGauge, dial: { cx: number; cy: number; r: number; radius: number; sweep: number; lineWidth: number }, within: Rect, options: { bottom?: number; halfWidth?: number } = {}) : boolean {
    const { cx, cy, r, radius, sweep, lineWidth } = dial;
    if (sweep > 300 || sweep <= 180) {
        return false;
    }
    if (!hasSecondText(gauge)) {
        return true;
    }

    const half = (sweep / 2) * (Math.PI / 180);
    const gap = Math.max(0, Math.min(options.halfWidth ?? Infinity, r * Math.sin(half) - lineWidth / 2 - radius * 0.04));
    const height = radius * SECOND_HEIGHT;
    const foot = Math.min(options.bottom ?? cy - r * Math.cos(half) + lineWidth / 2, within.top + within.height);
    secondText(ctx, gauge, { left: cx - gap, top: foot - height, width: gap * 2, height }, 'center', 'bottom');

    return true;
}

/** The second value at the bottom inside a full circle of free radius `inner` (ring, tank). */
function insideSecond(ctx: Context, gauge: StyledGauge, cx: number, cy: number, inner: number) : void {
    if (hasSecondText(gauge)) {
        secondText(ctx, gauge, { left: cx - inner * 0.6, top: cy + inner * 0.6, width: inner * 1.2, height: inner * 0.19 });
    }
}

/** The second value as a number (the owner), in its colour, where the box has room for it. */
function secondText(ctx: Context, gauge: StyledGauge, box: Rect, align: 'center' | 'left' | 'right' = 'center', valign: 'top' | 'bottom' = 'top') : void {
    const second = gauge.hasSecond ? gauge.readings[1] : undefined;
    if (!second?.text || box.height < 5 || box.width < 10) {
        return;
    }

    drawText(ctx, second.text, box, second.color ?? gauge.color, ink(gauge, { valign, align }));
}

/** The number and its unit on a line, right-aligned in `box`, for the straight styles. */
function numberLine(ctx: Context, gauge: StyledGauge, box: Rect, align: 'left' | 'right' | 'center' = 'right', withIcon = true) : void {
    if (gauge.icon && withIcon && align !== 'center') {
        const side = box.height * 0.7;
        drawIcon(ctx, gauge.icon, { left: align === 'right' ? box.left : box.left + box.width - side, top: box.top + (box.height - side) / 2, width: side, height: side }, gauge.iconColor, { fill: true });
    }
    // The number as high as the box, its unit beside its top; both smaller together where they are
    // wider than the box (a narrow portrait tile), measured rather than given fixed shares of it.
    const iconRoom = gauge.icon && withIcon && align !== 'center' ? box.height * 0.85 : 0;
    const room = Math.max(0, box.width - iconRoom);
    const numberStyle = ink(gauge, { align: 'left', valign: 'bottom' });
    const unitStyle = ink(gauge, { align: 'left', valign: 'top' });
    const unitShare = 0.32 * gauge.unitScale;
    const widthAt = (height: number) : { number: number; unit: number; gap: number } => ({
        number: layoutText(ctx, gauge.numberText, 1e6, height, numberStyle).bounds.width,
        unit: gauge.unitText === '' ? 0 : layoutText(ctx, gauge.unitText, 1e6, height * unitShare, unitStyle).bounds.width,
        gap: gauge.unitText === '' ? 0 : height * 0.1,
    });
    let height = box.height;
    let widths = widthAt(height);
    const total = widths.number + widths.gap + widths.unit;
    if (total > room && total > 0) {
        height *= room / total;
        widths = widthAt(height);
    }
    const width = widths.number + widths.gap + widths.unit;
    const left = align === 'right' ? box.left + box.width - width : align === 'left' ? box.left + iconRoom : box.left + (box.width - width) / 2;
    const top = box.top + box.height - height;
    drawText(ctx, gauge.numberText, { left, top, width: widths.number + 1, height }, gauge.color, numberStyle);
    if (widths.unit > 0) {
        drawText(ctx, gauge.unitText, { left: left + widths.number + widths.gap, top, width: widths.unit + 1, height: height * unitShare }, gauge.color, unitStyle);
    }
}

/**
 * The height of a dial's minimum and maximum, as a share of its radius: their capitals about an
 * eighth of it, near the second value's size (the owner, 2026-10-22: at 0.11 they were far too small).
 */
const END_LABEL = 0.19;

/** The second value's height at a dial's bottom, as a share of its radius: a little above the end numbers. */
const SECOND_HEIGHT = 0.15;

/**
 * The scale numbers of the straight styles, against the second value's height beside them: their
 * capitals a little smaller than its digits (the owner, 2026-10-22).
 */
const STRAIGHT_LABEL = 1.45;

/** A scale number centred on `x`, its box narrowed to stay inside `within`. */
function scaleNumber(ctx: Context, gauge: StyledGauge, value: number, x: number, top: number, size: number, within: Rect) : void {
    const half = Math.max(0, Math.min(size * 2, x - within.left, within.left + within.width - x));
    drawText(ctx, scaleText(gauge, value), { left: x - half, top, width: half * 2, height: size }, DIM, ink(gauge, { fill: false }));
}

/** The baseline of scale numbers drawn from `top` with a box `size` high: for a text beside them on their line. */
function scaleBaseline(ctx: Context, gauge: StyledGauge, top: number, size: number) : number {
    return top + layoutText(ctx, '0', 1e6, size, ink(gauge, { fill: false })).baseline;
}

/** The minimum and maximum under the ends of a dial, where there is room. */
function endLabels(ctx: Context, gauge: StyledGauge, cx: number, cy: number, r: number, sweep: number, size: number, within: Rect) : void {
    const first = gauge.readings[0];
    if (!first || size < 7 || gauge.look.numbers === false) {
        return;
    }

    for (const [degrees, value] of [[-sweep / 2, first.min], [sweep / 2, first.max]] as const) {
        const [x, y] = point(cx, cy, r, degrees);
        scaleNumber(ctx, gauge, value, x, y + size * 0.45, size, within);
    }
}

/** A small mark at a place on a dial: the second value on the scale. */
function dialMark(ctx: Context, cx: number, cy: number, r: number, degrees: number, length: number, color: string, width: number) : void {
    const [x0, y0] = point(cx, cy, r - length / 2, degrees);
    const [x1, y1] = point(cx, cy, r + length / 2, degrees);
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    stroke(ctx, color, width, 'round');
}

// ---- The styles -----------------------------------------------------------------------------------

type Drawer = (ctx: Context, box: Rect, gauge: StyledGauge) => void;

/** A thick track filled up to the value in the colour of its place (a threshold's, or the gradient's). */
const arc: Drawer = (ctx, box, gauge) => {
    const sweep = gauge.look.sweep ?? 240;
    const { cx, cy, radius } = fitDial(box, sweep, gauge.look.numbers === false ? box.height * 0.06 : box.height * 0.17);
    const width = radius * ((gauge.look.thickness ?? 13) / 100);
    const r = radius - width / 2;
    const cap: CanvasLineCap = gauge.look.roundEnds === false ? 'butt' : 'round';
    const [from, to] = [-sweep / 2, sweep / 2];
    const track = trackOf(gauge);
    if (track) {
        arcPath(ctx, cx, cy, r, from, to);
        stroke(ctx, track, width, cap);
    }
    const position = positionOf(gauge.readings[0]);
    if (position !== undefined && position > 0) {
        arcPath(ctx, cx, cy, r, from, from + sweep * position);
        stroke(ctx, readingColor(gauge, gauge.readings[0]), width, cap);
    }
    const second = secondPlace(gauge);
    if (second !== undefined) {
        dialMark(ctx, cx, cy, r, from + sweep * second, width * 1.4, gauge.readings[1]?.color ?? '#ffffff', Math.max(2, width * 0.18));
    }
    endLabels(ctx, gauge, cx, cy, r, sweep, radius * END_LABEL, box);
    // At the bottom between the minimum and the maximum, its foot on theirs.
    const labels = gauge.look.numbers !== false && radius * END_LABEL >= 7;
    const ends = { bottom: point(cx, cy, r, sweep / 2)[1] + radius * END_LABEL * 1.45, halfWidth: r * Math.sin((sweep / 2) * (Math.PI / 180)) - radius * 0.3 };
    const atBottom = gapSecond(ctx, gauge, { cx, cy, r, radius, sweep, lineWidth: width }, box, labels ? ends : {});
    centreTexts(ctx, gauge, cx, cy + radius * 0.05, r - width, { left: box.left, top: box.top, width: box.width, height: box.height * 0.98 }, !atBottom);
};

/** The whole scale in its colours, a knob at the value, a dot for the second (a thermostat's look). */
const marker: Drawer = (ctx, box, gauge) => {
    const sweep = gauge.look.sweep ?? 270;
    const { cx, cy, radius } = fitDial(box, sweep);
    const width = radius * ((gauge.look.thickness ?? 9) / 100);
    const r = radius - width * 1.2;
    const [from, to] = [-sweep / 2, sweep / 2];
    scaleArc(ctx, gauge, cx, cy, r, from, to, 0, 1, width);
    if (gauge.look.roundEnds !== false) {
        roundCap(ctx, ...point(cx, cy, r, from), width, scaleColor(gauge, 0));
        roundCap(ctx, ...point(cx, cy, r, to), width, scaleColor(gauge, 1));
    }
    const second = secondPlace(gauge);
    if (second !== undefined) {
        roundCap(ctx, ...point(cx, cy, r, from + sweep * second), width * 0.45, gauge.readings[1]?.color ?? '#151515');
    }
    const position = positionOf(gauge.readings[0]);
    if (position !== undefined) {
        const [x, y] = point(cx, cy, r, from + sweep * position);
        roundCap(ctx, x, y, width * 2.6, '#151515');
        roundCap(ctx, x, y, width * 2.1, gauge.look.accent ?? '#ffffff');
    }
    const atBottom = gapSecond(ctx, gauge, { cx, cy, r, radius, sweep, lineWidth: width }, box);
    centreTexts(ctx, gauge, cx, cy, r - width * 2, box, !atBottom);
};

/** An analogue meter: bands, ticks with numbers, a needle on a hub; the number below the hub. */
const needle: Drawer = (ctx, box, gauge) => {
    const sweep = gauge.look.sweep ?? 240;
    const { cx, cy, radius } = fitDial(box, sweep, box.height * 0.3);
    const first = gauge.readings[0];
    const [from, to] = [-sweep / 2, sweep / 2];
    const band = radius * ((gauge.look.thickness ?? 4) / 100);
    // The bands along the edge: the thresholds' colours where there are any, else the gradient.
    scaleArc(ctx, gauge, cx, cy, radius - band / 2, from, to, 0, 1, band);
    const majors = gauge.look.steps ?? 4;
    const minors = majors * 5;
    for (let index = 0; index <= minors; index++) {
        const major = index % 5 === 0;
        const degrees = from + (sweep * index) / minors;
        const [x0, y0] = point(cx, cy, radius - band * 1.6 - (major ? radius * 0.13 : radius * 0.07), degrees);
        const [x1, y1] = point(cx, cy, radius - band * 1.6, degrees);
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        stroke(ctx, '#dddddd', major ? Math.max(1.5, radius * 0.022) : Math.max(1, radius * 0.01));
        if (major && first && gauge.look.numbers !== false && radius > 40) {
            const [lx, ly] = point(cx, cy, radius * 0.62, degrees);
            const size = radius * 0.15;
            drawText(ctx, scaleText(gauge, first.min + ((first.max - first.min) * index) / minors), { left: lx - size * 2, top: ly - size / 2, width: size * 4, height: size }, '#bbbbbb', ink(gauge, { fill: false }));
        }
    }
    const pointer = (position: number, color: string, length: number, base: number) : void => {
        const degrees = from + sweep * position;
        const [tx, ty] = point(cx, cy, length, degrees);
        const [lx, ly] = point(cx, cy, base, degrees - 90);
        const [rx, ry] = point(cx, cy, base, degrees + 90);
        const [bx, by] = point(cx, cy, base * 2.2, degrees + 180);
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(lx, ly);
        ctx.lineTo(bx, by);
        ctx.lineTo(rx, ry);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
    };
    const second = secondPlace(gauge);
    if (second !== undefined) {
        // The second value: a thin, short needle under the first.
        pointer(second, gauge.readings[1]?.color ?? '#7fb2ff', radius * 0.62, radius * 0.035);
    }
    const position = positionOf(first);
    if (position !== undefined) {
        pointer(position, gauge.look.accent ?? '#ff4d3d', radius * 0.84, radius * 0.07);
    }
    roundCap(ctx, cx, cy, radius * 0.18, '#dddddd');
    roundCap(ctx, cx, cy, radius * 0.07, '#151515');
    if (gauge.icon) {
        const side = radius * 0.2;
        drawIcon(ctx, gauge.icon, { left: cx - side / 2, top: cy - radius * 0.42, width: side, height: side }, gauge.iconColor, { fill: true });
    }
    // Below the scale's lowest numbers, which sit at the ends.
    const under = cy + radius * 0.42;
    const room = box.top + box.height - under;
    // The number, then the second value with a clear gap (the Mac, 2026-10-22: they touched in a wide tile).
    const numberHeight = Math.min(room * 0.5, radius * 0.34);
    numberLine(ctx, gauge, { left: cx - radius * 0.7, top: under, width: radius * 1.4, height: numberHeight }, 'center');
    secondText(ctx, gauge, { left: cx - radius * 0.6, top: under + numberHeight + room * 0.14, width: radius * 1.2, height: Math.min(room * 0.24, radius * 0.15) });
};

/** A thick half circle filled up to the value; minimum and maximum at its feet; the number inside. */
const half: Drawer = (ctx, box, gauge) => {
    const { cx, cy, radius } = fitDial(box, 180, gauge.look.numbers === false ? box.height * 0.2 : box.height * 0.26);
    const width = radius * ((gauge.look.thickness ?? 24) / 100);
    const r = radius - width / 2;
    const track = trackOf(gauge);
    if (track) {
        arcPath(ctx, cx, cy, r, -90, 90);
        stroke(ctx, track, width);
    }
    const position = positionOf(gauge.readings[0]);
    if (position !== undefined && position > 0) {
        if (gauge.coloring === 'threshold' && gauge.thresholds.length > 0) {
            arcPath(ctx, cx, cy, r, -90, -90 + 180 * position);
            stroke(ctx, readingColor(gauge, gauge.readings[0]), width);
        } else {
            scaleArc(ctx, gauge, cx, cy, r, -90, -90 + 180 * position, 0, position, width);
        }
    }
    const second = secondPlace(gauge);
    if (second !== undefined) {
        dialMark(ctx, cx, cy, r, -90 + 180 * second, width * 1.15, gauge.readings[1]?.color ?? '#ffffff', Math.max(2, width * 0.12));
    }
    const first = gauge.readings[0];
    const secondHeight = radius * 0.12;
    // The second value at the bottom between the feet: on the minimum's and maximum's line.
    let secondTop = cy + radius * 0.06;
    if (first && gauge.look.numbers !== false) {
        const size = radius * END_LABEL;
        scaleNumber(ctx, gauge, first.min, cx - r, cy + size * 0.3, size, box);
        scaleNumber(ctx, gauge, first.max, cx + r, cy + size * 0.3, size, box);
        secondTop = scaleBaseline(ctx, gauge, cy + size * 0.3, size) - secondHeight;
    }
    const inner = r - width / 2;
    numberLine(ctx, gauge, { left: cx - inner * 0.75, top: cy - inner * 0.55, width: inner * 1.5, height: inner * 0.5 }, 'center', false);
    secondText(ctx, gauge, { left: cx - inner * 0.6, top: secondTop, width: inner * 1.2, height: secondHeight }, 'center', 'bottom');
    if (gauge.icon) {
        const side = inner * 0.28;
        drawIcon(ctx, gauge.icon, { left: cx - side / 2, top: cy - inner * 0.9, width: side, height: side }, gauge.iconColor, { fill: true });
    }
};

/** A full circle from the top, filled clockwise up to the value. */
const ring: Drawer = (ctx, box, gauge) => {
    const { cx, cy, radius } = fitDial(box, 360);
    const width = radius * ((gauge.look.thickness ?? 12) / 100);
    const r = radius - width / 2;
    const track = trackOf(gauge);
    if (track) {
        arcPath(ctx, cx, cy, r, 0, 360);
        stroke(ctx, track, width);
    }
    const position = positionOf(gauge.readings[0]);
    if (position !== undefined && position > 0) {
        arcPath(ctx, cx, cy, r, 0, 360 * position);
        stroke(ctx, readingColor(gauge, gauge.readings[0]), width, gauge.look.roundEnds === false ? 'butt' : 'round');
    }
    const second = secondPlace(gauge);
    if (second !== undefined) {
        dialMark(ctx, cx, cy, r, 360 * second, width * 1.3, gauge.readings[1]?.color ?? '#ffffff', Math.max(2, width * 0.16));
    }
    centreTexts(ctx, gauge, cx, cy - (hasSecondText(gauge) ? (r - width) * 0.08 : 0), r - width, box, false);
    insideSecond(ctx, gauge, cx, cy, r - width);
};

/** One ring for each value, nested, each in its own colour (the owner: the gauge with several values). */
const rings: Drawer = (ctx, box, gauge) => {
    const { cx, cy, radius } = fitDial(box, 360);
    const count = Math.max(1, gauge.readings.length);
    const width = radius * ((gauge.look.thickness ?? Math.min(15, 45 / count)) / 100);
    const gap = width * 0.25;
    gauge.readings.forEach((reading, index) => {
        const r = radius - width / 2 - index * (width + gap);
        if (r <= width) {
            return;
        }
        const color = reading.color ?? (index === 0 ? readingColor(gauge, reading) : RING_COLORS[index % RING_COLORS.length]!);
        arcPath(ctx, cx, cy, r, 0, 360);
        ctx.globalAlpha = 0.22;
        stroke(ctx, color, width);
        ctx.globalAlpha = 1;
        const position = positionOf(reading);
        if (position !== undefined && position > 0) {
            arcPath(ctx, cx, cy, r, 0, 360 * position);
            stroke(ctx, color, width, 'round');
        }
    });
    // Inside the smallest ring: the first number, then each other value's, in its ring's colour.
    const inner = Math.max(0, radius - count * (width + gap));
    const lines = gauge.readings.slice(1).filter((reading) => reading.text);
    const numberHeight = inner * (lines.length === 0 ? 0.55 : lines.length === 1 ? 0.45 : 0.38);
    const lineHeight = Math.min(inner * 0.2, (inner * 1.1 - numberHeight) / Math.max(1, lines.length));
    let top = cy - (numberHeight + lines.length * lineHeight * 1.15) / 2;
    numberLine(ctx, gauge, { left: cx - inner * 0.8, top, width: inner * 1.6, height: numberHeight }, 'center', false);
    top += numberHeight + lineHeight * 0.25;
    gauge.readings.slice(1).forEach((reading, index) => {
        if (!reading.text || lineHeight < 4) {
            return;
        }
        const color = reading.color ?? RING_COLORS[(index + 1) % RING_COLORS.length]!;
        drawText(ctx, reading.text, { left: cx - inner * 0.7, top, width: inner * 1.4, height: lineHeight }, color, ink(gauge, { valign: 'top' }));
        top += lineHeight * 1.15;
    });
};

/** An arc of separate blocks, lit up to the value (an LED meter). */
const blocks: Drawer = (ctx, box, gauge) => {
    const sweep = gauge.look.sweep ?? 250;
    const { cx, cy, radius } = fitDial(box, sweep);
    const width = radius * ((gauge.look.thickness ?? 18) / 100);
    const r = radius - width / 2;
    const count = gauge.look.steps ?? 15;
    const gap = Math.min(4, sweep / count / 4);
    const span = (sweep - gap * (count - 1)) / count;
    const position = positionOf(gauge.readings[0]) ?? -1;
    const off = trackOf(gauge, 'rgba(255, 255, 255, 0.1)');
    for (let index = 0; index < count; index++) {
        const start = -sweep / 2 + index * (span + gap);
        const middle = (index + 0.5) / count;
        const lit = middle <= position;
        if (!lit && !off) {
            continue;
        }
        arcPath(ctx, cx, cy, r, start, start + span);
        stroke(ctx, lit ? scaleColor(gauge, middle) : off!, width);
    }
    const atBottom = gapSecond(ctx, gauge, { cx, cy, r, radius, sweep, lineWidth: width }, box);
    centreTexts(ctx, gauge, cx, cy, r - width, box, !atBottom);
};

/** A thin bright line with a glow up to the value, the rest barely there (a dark wall at night). */
const glow: Drawer = (ctx, box, gauge) => {
    const sweep = gauge.look.sweep ?? 270;
    const { cx, cy, radius } = fitDial(box, sweep);
    const width = radius * ((gauge.look.thickness ?? 3) / 100);
    const r = radius - radius * 0.1;
    const [from] = [-sweep / 2];
    const track = trackOf(gauge, 'rgba(120, 160, 170, 0.18)');
    if (track) {
        arcPath(ctx, cx, cy, r, from, sweep / 2);
        stroke(ctx, track, width * 0.8, 'round');
    }
    const color = gauge.look.accent ?? (gauge.coloring === 'threshold' && gauge.thresholds.length > 0 ? readingColor(gauge, gauge.readings[0]) : '#00e5ff');
    const second = secondPlace(gauge);
    if (second !== undefined) {
        roundCap(ctx, ...point(cx, cy, r, from + sweep * second), width * 2.2, gauge.readings[1]?.color ?? 'rgba(255, 255, 255, 0.55)');
    }
    const position = positionOf(gauge.readings[0]);
    if (position !== undefined && position > 0) {
        ctx.save();
        ctx.shadowColor = color;
        ctx.shadowBlur = radius * 0.12;
        arcPath(ctx, cx, cy, r, from, from + sweep * position);
        stroke(ctx, color, width, 'round');
        stroke(ctx, color, width, 'round');
        roundCap(ctx, ...point(cx, cy, r, from + sweep * position), width * 3, '#e0ffff');
        ctx.restore();
    }
    const atBottom = gapSecond(ctx, gauge, { cx, cy, r, radius, sweep, lineWidth: width * 3 }, box);
    centreTexts(ctx, gauge, cx, cy, r - radius * 0.08, box, !atBottom);
};

/** A straight bar, or cells, filled up to the value; number above, minimum and maximum below. */
function straight(cells: boolean) : Drawer {
    return (ctx, box, gauge) => {
        const vertical = gauge.look.vertical === true;
        const position = positionOf(gauge.readings[0]);
        const second = secondPlace(gauge);
        const first = gauge.readings[0];
        const pad = Math.min(box.width, box.height) * 0.06;
        if (vertical) {
            const thickness = Math.min(box.width * 0.3, box.width * ((gauge.look.thickness ?? 22) / 100) * 1.5);
            const bar = { left: box.left + pad, top: box.top + pad, width: thickness, height: box.height - 2 * pad };
            drawBar(ctx, gauge, bar, position, second, cells, true);
            const textLeft = bar.left + bar.width + pad * 2;
            const textWidth = box.left + box.width - textLeft - pad;
            numberLine(ctx, gauge, { left: textLeft, top: box.top + box.height * 0.3, width: textWidth, height: box.height * 0.26 }, 'left');
            // At the bottom, level with the bar's foot.
            secondText(ctx, gauge, { left: textLeft, top: bar.top + bar.height - box.height * 0.1, width: textWidth, height: box.height * 0.1 }, 'left');

            return;
        }

        const height = box.height * ((gauge.look.thickness ?? (cells ? 16 : 11)) / 100);
        const bar = { left: box.left + pad, top: box.top + box.height * 0.52, width: box.width - 2 * pad, height };
        numberLine(ctx, gauge, { left: bar.left, top: box.top + box.height * 0.12, width: bar.width, height: box.height * 0.3 }, 'right');
        drawBar(ctx, gauge, bar, position, second, cells, false);
        const below = bar.top + bar.height + box.height * 0.08;
        const size = box.height * 0.08;
        endsAndSecond(ctx, gauge, bar.left, bar.left + bar.width, below, size);
    };
}

/**
 * Under a straight scale from `x0` to `x1`: its minimum at the left, its maximum at the right, and
 * the second value at the bottom between them, in the room they leave (measured: in a narrow tile
 * the numbers take more of the width).
 */
function endsAndSecond(ctx: Context, gauge: StyledGauge, x0: number, x1: number, top: number, size: number) : void {
    const first = gauge.readings[0];
    const third = (x1 - x0) / 3;
    let [left, right] = [x0 + third * 0.66, x1 - third * 0.66];
    const height = size * 1.1;
    let secondTop = top;
    if (first && gauge.look.numbers !== false) {
        // On their line.
        secondTop = scaleBaseline(ctx, gauge, top, size * STRAIGHT_LABEL) - height;
        const low = drawText(ctx, scaleText(gauge, first.min), { left: x0, top, width: third, height: size * STRAIGHT_LABEL }, DIM, ink(gauge, { fill: false, align: 'left' }));
        const high = drawText(ctx, scaleText(gauge, first.max), { left: x1 - third, top, width: third, height: size * STRAIGHT_LABEL }, DIM, ink(gauge, { fill: false, align: 'right' }));
        const margin = size * 0.5;
        [left, right] = [x0 + low.left + low.width + margin, x1 - third + high.left - margin];
    }
    // Centred on the scale, as wide as the room on its narrower side allows.
    const middle = (x0 + x1) / 2;
    const half = Math.max(0, Math.min(middle - left, right - middle));
    secondText(ctx, gauge, { left: middle - half, top: secondTop, width: half * 2, height }, 'center', 'bottom');
}

function drawBar(ctx: Context, gauge: StyledGauge, bar: Rect, position: number | undefined, second: number | undefined, cells: boolean, vertical: boolean) : void {
    const length = vertical ? bar.height : bar.width;
    const along = (p: number) : Rect => (vertical ? { left: bar.left, top: bar.top + bar.height * (1 - p), width: bar.width, height: bar.height * p } : { left: bar.left, top: bar.top, width: bar.width * p, height: bar.height });
    const rounded = gauge.look.roundEnds === false ? 0 : Math.min(bar.width, bar.height) * 0.2;
    if (cells) {
        const count = gauge.look.steps ?? 20;
        const gap = Math.max(1.5, length / count * 0.18);
        const cell = (length - gap * (count - 1)) / count;
        const off = trackOf(gauge, 'rgba(255, 255, 255, 0.1)');
        for (let index = 0; index < count; index++) {
            const middle = (index + 0.5) / count;
            const lit = position !== undefined && middle <= position;
            if (!lit && !off) {
                continue;
            }
            ctx.fillStyle = lit ? scaleColor(gauge, middle) : off!;
            const offset = index * (cell + gap);
            if (vertical) {
                ctx.fillRect(bar.left, bar.top + bar.height - offset - cell, bar.width, cell);
            } else {
                ctx.fillRect(bar.left + offset, bar.top, cell, bar.height);
            }
        }
    } else {
        const track = trackOf(gauge);
        if (track) {
            ctx.fillStyle = track;
            ctx.beginPath();
            ctx.roundRect(bar.left, bar.top, bar.width, bar.height, rounded);
            ctx.fill();
        }
        if (position !== undefined && position > 0) {
            const filled = along(position);
            if (gauge.coloring === 'threshold' && gauge.thresholds.length > 0) {
                ctx.fillStyle = readingColor(gauge, gauge.readings[0]);
            } else {
                const gradient = vertical ? ctx.createLinearGradient(0, bar.top + bar.height, 0, bar.top) : ctx.createLinearGradient(bar.left, 0, bar.left + bar.width, 0);
                for (let index = 0; index <= 16; index++) {
                    gradient.addColorStop(index / 16, scaleColor(gauge, index / 16));
                }
                ctx.fillStyle = gradient;
            }
            ctx.beginPath();
            ctx.roundRect(filled.left, filled.top, filled.width, filled.height, rounded);
            ctx.fill();
        }
    }
    if (second !== undefined) {
        // The second value: a mark across the bar, a target.
        ctx.fillStyle = gauge.readings[1]?.color ?? '#ffffff';
        const thick = Math.max(2, Math.min(bar.width, bar.height) * 0.12);
        if (vertical) {
            ctx.fillRect(bar.left - bar.width * 0.2, bar.top + bar.height * (1 - second) - thick / 2, bar.width * 1.4, thick);
        } else {
            // Overhanging the bar a little, clear of the number above and the texts below.
            ctx.fillRect(bar.left + bar.width * second - thick / 2, bar.top - bar.height * 0.18, thick, bar.height * 1.36);
        }
    }
}

/** A tube with a bulb, filled to the value; a scale beside it; the number to the right. */
const thermometer: Drawer = (ctx, box, gauge) => {
    const first = gauge.readings[0];
    const tube = Math.min(box.width * 0.11, box.height * 0.11);
    const cx = box.left + box.width * 0.28;
    const top = box.top + box.height * 0.06 + tube / 2;
    const bulb = tube * 1.8;
    const bottom = box.top + box.height - bulb * 2;
    const color = gauge.look.accent ?? readingColor(gauge, first);
    const glass = trackOf(gauge, '#2a2a2a') ?? '#2a2a2a';
    ctx.fillStyle = glass;
    ctx.beginPath();
    ctx.roundRect(cx - tube / 2, top - tube / 2, tube, bottom - top + tube, tube / 2);
    ctx.fill();
    roundCap(ctx, cx, bottom + bulb * 0.7, bulb * 2, glass);
    const position = positionOf(first);
    const level = bottom - (bottom - top) * (position ?? 0);
    ctx.fillStyle = color;
    ctx.fillRect(cx - tube * 0.27, level, tube * 0.54, bottom - level + bulb * 0.5);
    roundCap(ctx, cx, bottom + bulb * 0.7, bulb * 1.4, color);
    const steps = gauge.look.steps ?? 4;
    const size = Math.min(box.height * 0.06, tube * 0.9, box.width * 0.05) * STRAIGHT_LABEL;
    for (let index = 0; index <= steps; index++) {
        const y = bottom - ((bottom - top) * index) / steps;
        ctx.beginPath();
        ctx.moveTo(cx + tube * 0.8, y);
        ctx.lineTo(cx + tube * 1.4, y);
        stroke(ctx, DIM, 1.2);
        if (first && gauge.look.numbers !== false) {
            drawText(ctx, scaleText(gauge, first.min + ((first.max - first.min) * index) / steps), { left: cx + tube * 1.6, top: y - size / 2, width: size * 3, height: size }, DIM, ink(gauge, { fill: false, align: 'left' }));
        }
    }
    const second = secondPlace(gauge);
    if (second !== undefined) {
        const y = bottom - (bottom - top) * second;
        ctx.fillStyle = gauge.readings[1]?.color ?? '#ffffff';
        ctx.beginPath();
        ctx.moveTo(cx - tube * 0.75, y);
        ctx.lineTo(cx - tube * 1.4, y - tube * 0.4);
        ctx.lineTo(cx - tube * 1.4, y + tube * 0.4);
        ctx.closePath();
        ctx.fill();
    }
    const textLeft = cx + tube * 1.6 + size * 2.2;
    const textWidth = box.left + box.width - textLeft;
    numberLine(ctx, gauge, { left: textLeft, top: box.top + box.height * 0.32, width: textWidth, height: box.height * 0.24 }, 'center');
    // At the bottom, level with the bulb.
    secondText(ctx, gauge, { left: textLeft, top: bottom + bulb * 0.7 - box.height * 0.045, width: textWidth, height: box.height * 0.09 });
};

/** A round tank filled to the value, with a still wave on top; the number over it. */
const tank: Drawer = (ctx, box, gauge) => {
    const { cx, cy, radius } = fitDial(box, 360);
    const position = positionOf(gauge.readings[0]) ?? 0;
    const color = gauge.look.accent ?? readingColor(gauge, gauge.readings[0]);
    const outline = radius * 0.03;
    const inner = radius - outline * 3;
    ctx.beginPath();
    ctx.arc(cx, cy, radius - outline / 2, 0, Math.PI * 2);
    stroke(ctx, color, outline);
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, inner, 0, Math.PI * 2);
    ctx.clip();
    const track = trackOf(gauge, 'rgba(255, 255, 255, 0.06)');
    if (track) {
        ctx.fillStyle = track;
        ctx.fillRect(cx - inner, cy - inner, inner * 2, inner * 2);
    }
    const level = cy + inner - 2 * inner * position;
    const wave = gauge.look.roundEnds === false ? 0 : inner * 0.05;
    ctx.beginPath();
    ctx.moveTo(cx - inner, level);
    for (let x = -inner; x <= inner; x += Math.max(1, inner / 30)) {
        ctx.lineTo(cx + x, level + wave * Math.sin((x / inner) * Math.PI * 2.5));
    }
    ctx.lineTo(cx + inner, cy + inner);
    ctx.lineTo(cx - inner, cy + inner);
    ctx.closePath();
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = color;
    ctx.fill();
    ctx.restore();
    const second = secondPlace(gauge);
    if (second !== undefined) {
        // The second value: a short mark on the tank's wall at its level.
        const y = cy + inner - 2 * inner * second;
        const half = Math.sqrt(Math.max(0, inner * inner - (y - cy) ** 2));
        ctx.beginPath();
        ctx.moveTo(cx + half - inner * 0.18, y);
        ctx.lineTo(cx + half, y);
        stroke(ctx, gauge.readings[1]?.color ?? '#ffffff', Math.max(2, radius * 0.025), 'round');
    }
    centreTexts(ctx, gauge, cx, cy - (hasSecondText(gauge) ? inner * 0.08 : 0), inner * 0.9, box, false);
    insideSecond(ctx, gauge, cx, cy, inner * 0.9);
};

/** Stephen Few's bullet graph: bands for the thresholds, a bar for the value, a mark for the second. */
const bullet: Drawer = (ctx, box, gauge) => {
    const first = gauge.readings[0];
    const pad = box.width * 0.06;
    const bar = { left: box.left + pad, top: box.top + box.height * 0.42, width: box.width - 2 * pad, height: box.height * ((gauge.look.thickness ?? 18) / 100) };
    numberLine(ctx, gauge, { left: bar.left, top: box.top + box.height * 0.06, width: bar.width, height: box.height * 0.28 }, 'right');
    if (!first) {
        return;
    }
    const at = (value: number) : number => bar.left + bar.width * clamp01((value - first.min) / (first.max - first.min || 1));
    // The bands: the thresholds' bounds, in shades of grey, darker for the first.
    const bounds = [first.min, ...gauge.thresholds.map((threshold) => threshold.from).filter((from) => from > first.min && from < first.max).sort((a, b) => a - b), first.max];
    const plain = bounds.length === 2 ? [first.min, first.min + (first.max - first.min) / 3, first.min + ((first.max - first.min) * 2) / 3, first.max] : bounds;
    for (let index = 0; index < plain.length - 1; index++) {
        const shade = 0x4a - (index * 0x20) / Math.max(1, plain.length - 2);
        ctx.fillStyle = `rgb(${shade}, ${shade}, ${shade})`;
        ctx.fillRect(at(plain[index]!), bar.top, at(plain[index + 1]!) - at(plain[index]!) + 0.5, bar.height);
    }
    const position = positionOf(first);
    if (position !== undefined) {
        ctx.fillStyle = gauge.look.accent ?? '#ffffff';
        ctx.fillRect(bar.left, bar.top + bar.height * 0.35, bar.width * position, bar.height * 0.3);
    }
    const second = secondPlace(gauge);
    if (second !== undefined) {
        ctx.fillStyle = gauge.readings[1]?.color ?? '#ff9f0a';
        const thick = Math.max(2, bar.height * 0.08);
        ctx.fillRect(bar.left + bar.width * second - thick / 2, bar.top + bar.height * 0.12, thick, bar.height * 0.76);
    }
    const size = box.height * 0.075;
    if (gauge.look.numbers !== false) {
        const steps = gauge.look.steps ?? 4;
        for (let index = 0; index <= steps; index++) {
            const x = bar.left + (bar.width * index) / steps;
            ctx.fillStyle = DIM;
            ctx.fillRect(x - 0.5, bar.top + bar.height + 2, 1, size * 0.5);
            drawText(ctx, scaleText(gauge, first.min + ((first.max - first.min) * index) / steps), { left: x - size * 2, top: bar.top + bar.height + size * 0.7, width: size * 4, height: size * STRAIGHT_LABEL }, DIM, ink(gauge, { fill: false }));
        }
    }
    // At the bottom, under the scale's numbers.
    const secondHeight = size * 1.2;
    secondText(ctx, gauge, { left: bar.left + bar.width * 0.15, top: box.top + box.height - secondHeight, width: bar.width * 0.7, height: secondHeight });
};

/** An arc of dots, the ones up to the value larger and in their colour. */
const dots: Drawer = (ctx, box, gauge) => {
    const sweep = gauge.look.sweep ?? 270;
    const { cx, cy, radius } = fitDial(box, sweep);
    const count = gauge.look.steps ?? 27;
    const size = radius * ((gauge.look.thickness ?? 7) / 100);
    const r = radius - size;
    const position = positionOf(gauge.readings[0]) ?? -1;
    const second = secondPlace(gauge);
    const off = trackOf(gauge, 'rgba(255, 255, 255, 0.2)');
    for (let index = 0; index < count; index++) {
        const t = count === 1 ? 0 : index / (count - 1);
        const [x, y] = point(cx, cy, r, -sweep / 2 + sweep * t);
        const on = t <= position;
        if (second !== undefined && Math.abs(t - second) < 0.5 / (count - 1)) {
            roundCap(ctx, x, y, size * 1.9, gauge.readings[1]?.color ?? '#ffffff');
            roundCap(ctx, x, y, size * 1.2, on ? scaleColor(gauge, t) : '#151515');
            continue;
        }
        if (on || off) {
            roundCap(ctx, x, y, on ? size * 1.5 : size * 0.95, on ? scaleColor(gauge, t) : off!);
        }
    }
    const atBottom = gapSecond(ctx, gauge, { cx, cy, r, radius, sweep, lineWidth: size * 1.9 }, box);
    centreTexts(ctx, gauge, cx, cy, r - size * 2, box, !atBottom);
};

/** No dial: the number as large as fits, a thin line under it for its place in the range. */
const minimal: Drawer = (ctx, box, gauge) => {
    const pad = box.width * 0.12;
    if (gauge.icon) {
        const side = box.height * 0.16;
        drawIcon(ctx, gauge.icon, { left: box.left + (box.width - side) / 2, top: box.top + box.height * 0.04, width: side, height: side }, gauge.iconColor, { fill: true });
    }
    numberLine(ctx, gauge, { left: box.left + pad, top: box.top + box.height * 0.24, width: box.width - 2 * pad, height: box.height * 0.36 }, 'center');
    const y = box.top + box.height * 0.7;
    const thick = Math.max(2, box.height * ((gauge.look.thickness ?? 2.5) / 100));
    const [x0, x1] = [box.left + pad, box.left + box.width - pad];
    const cap: CanvasLineCap = gauge.look.roundEnds === false ? 'butt' : 'round';
    const track = trackOf(gauge);
    if (track) {
        ctx.beginPath();
        ctx.moveTo(x0, y);
        ctx.lineTo(x1, y);
        stroke(ctx, track, thick, cap);
    }
    const position = positionOf(gauge.readings[0]);
    if (position !== undefined && position > 0) {
        ctx.beginPath();
        ctx.moveTo(x0, y);
        ctx.lineTo(x0 + (x1 - x0) * position, y);
        stroke(ctx, readingColor(gauge, gauge.readings[0]), thick, cap);
    }
    const second = secondPlace(gauge);
    if (second !== undefined) {
        ctx.fillStyle = gauge.readings[1]?.color ?? '#ffffff';
        ctx.fillRect(x0 + (x1 - x0) * second - 1, y - thick * 2, 2, thick * 4);
    }
    const first = gauge.readings[0];
    const size = box.height * 0.07;
    endsAndSecond(ctx, gauge, x0, x1, y + thick * 2, size);
};

const DRAWERS: Readonly<Record<StyledGauge['style'], Drawer>> = {
    arc,
    marker,
    needle,
    half,
    ring,
    rings,
    blocks,
    glow,
    bar: straight(false),
    cells: straight(true),
    thermometer,
    tank,
    bullet,
    dots,
    minimal,
};

/** Draws a gauge in one of the styles beside the legacy dial, its texts included, in `box`. */
export function drawStyledGauge(ctx: Context, box: Rect, gauge: StyledGauge) : void {
    if (box.width <= 0 || box.height <= 0) {
        return;
    }

    ctx.save();
    DRAWERS[gauge.style](ctx, box, gauge);
    ctx.restore();
}
